import React, { useCallback, useEffect, useMemo } from 'react';
import { useBrainCanvas } from '../../context/BrainCanvasContext';
import { dispatchTasksUpdatedEvent } from '../../hooks/campaignNesting/utils';
import { applyBrainModifyPreview, prepareClipboardPaste } from '../../utils/brainClipboard';
import { schedulePasteAutoLayoutAfterMeasure } from './hooks/handlers/paste/schedulePasteAutoLayout';
import SprintDetailContent from './components/SprintDetailContent';
import {
  useSprintDetailCore,
  useSprintDetailFeatures,
  useSprintDetailViews,
  useSprintDetailRenderHandlers,
} from './hooks';
import useTabMissionOverlay from './hooks/useTabMissionOverlay';
import useCampaignVariables from './hooks/useCampaignVariables';
import useSprintDetailIntegrations from './hooks/useSprintDetailIntegrations';
import { calculateLockStatesWithParentContext } from './FlowchartView/utils/dependencyUtils';
import { useWaitGateNow } from './shared/ConditionsDropdown/utils/useWaitGateNow';
import { useWaitUnlockNotifications } from '../../hooks/useWaitUnlockNotifications';
import { useMissionReminderWatch } from '../../hooks/useMissionReminderWatch';
import { useClockTicketSync } from '../../hooks/useClockTicketSync';
import './styles/index.css';

/**
 * SprintDetail Component
 *
 * Unified view for both Sprints and Templates.
 * Renders a flowchart-based task management interface.
 * Supports nested sprints with drill-down navigation.
 */
const SprintDetail = ({
  sprint,
  allSprints = [],
  isTemplate = false,
  isLoadingContent = false,
  onUseTemplate,
  onSelectSprint,
  onAddSprint,
  onUpdateSprint,
  onRenameSprint,
  onSprintIconChange,
  onTrashSprint,
  onNestCampaign,
  onCreateNestedCampaign,
  loadFullSprint,
  settings = {},
  initialView = null,
  initialMissionFocus = null,
  onViewChanged,
  onMissionFocused,
  onViewModeChange,
  onCustomViewChange,
  activeView = null,
  multiCampaignData = null,
  onRenameView = null,
  onViewIconChange = null,
  onRemoveCampaignFromView = null,
  onAddCampaignToView = null,
  removingCampaignId = null,
  flowViewVisibility = {},
  onFlowViewVisibilityChange,
  hideViewExplainerModal = false,
  onHideViewExplainerModalChange,
  sidebarCollapsed = false,
}) => {
  const core = useSprintDetailCore({
    sprint, allSprints, onUpdateSprint, activeView, multiCampaignData,
  });

  const {
    isMultiCampaignMode, state: coreState, localSprint, setLocalSprint, actions, recordAction,
    undo, redo, undoTo, redoTo, undoAll, clearHistory,
    startBatch, endBatch, wrapLastUndoAction, undoStack, redoStack, canUndo, canRedo,
  } = core;
  const { register } = useBrainCanvas();

  // Mission Full View + doc stack live on the active tab (session-only), so
  // switching tabs keeps each tab's overlay state instead of leaking local state
  // across the stable SprintDetail instance.
  const {
    openMission,
    closeMission,
    setMissionDocStack,
    restoredDocStack,
    skipSurfaceAnimation,
    consumeSkipSurfaceAnimation,
    tabOpenMissionId,
    missionDocDirect,
  } = useTabMissionOverlay(coreState.detailSidebarTaskId, coreState.setDetailSidebarTaskId);

  const setDetailSidebarTaskId = useCallback((taskId, options) => {
    if (taskId == null) closeMission();
    else openMission(taskId, options);
  }, [openMission, closeMission]);

  const state = useMemo(
    () => ({ ...coreState, setDetailSidebarTaskId }),
    [coreState, setDetailSidebarTaskId]
  );

  const features = useSprintDetailFeatures({
    localSprint, setLocalSprint, actions, state,
    allSprints, onSprintIconChange, onSelectSprint, onAddSprint, onUpdateSprint,
    initialView, initialMissionFocus, targetCampaignId: sprint?.id || null,
    onViewChanged, onMissionFocused, onViewModeChange,
    onCustomViewChange,
    settings,
    isMultiCampaignMode,
    activeView,
  });

  const {
    setup, wrappedToolbar, modals, nested, fokusModeRefs, campaignViews,
    effectiveFlowViewVisibility, handleFlowViewVisibilityChangeInternal, customViewSettings,
  } = features;

  const views = useSprintDetailViews({
    localSprint, setLocalSprint, actions, recordAction, wrapLastUndoAction, startBatch, endBatch,
    isMultiCampaignMode, undo, redo, canUndo, canRedo, undoStack, redoStack,
    setup, wrappedToolbar, modals, nested, fokusModeRefs, campaignViews,
    effectiveFlowViewVisibility, handleFlowViewVisibilityChangeInternal, customViewSettings,
    state, allSprints, isTemplate, settings,
    onSelectSprint, onAddSprint, onUpdateSprint, onRenameSprint, onSprintIconChange,
    onTrashSprint, onNestCampaign, loadFullSprint,
    activeView, multiCampaignData, onRenameView, onViewIconChange, onRemoveCampaignFromView, onAddCampaignToView, removingCampaignId,
    hideViewExplainerModal, onHideViewExplainerModalChange,
  });

  const {
    handlers, handleViewRename, handleViewIconChangeLocal, handleCampaignPickerSelect,
    crossSprintActions, effectiveHandlers, handleConfirmDeleteTaskWithNested,
    handleDeleteTasksWithNestedCheck, handleTrashNestedSprint,
    calculatedProgress, flowProps, augmentedKanbanProps, augmentedTableProps,
    todoListProps, viewSwitcherProps, neuralFilteredTasks, inProgressLockWarningModal,
    renameFieldModal, setRenameFieldModal,
  } = views;

  useEffect(() => {
    return register({
      undo,
      applyPatch: (patch) => {
        if (!patch?.campaignId || !Array.isArray(patch.tasks)) return;
        if (localSprint?.id !== patch.campaignId) return;
        const before = patch.tasksBefore;
        recordAction?.({
          type: 'BRAIN_UPDATE_MISSION',
          description: `${patch.mission || 'Mission'}: updated`,
          undo: () => {
            if (Array.isArray(before)) dispatchTasksUpdatedEvent(patch.campaignId, before);
          },
          redo: () => dispatchTasksUpdatedEvent(patch.campaignId, patch.tasks),
        });
        dispatchTasksUpdatedEvent(patch.campaignId, patch.tasks);
      },
      applyClipboard: async (preview) => {
        if (preview.campaignId && localSprint?.id !== preview.campaignId) return false;
        if (preview.mode === 'modify') {
          const planned = applyBrainModifyPreview(preview, localSprint?.tasks || []);
          if (!planned) return false;
          const before = localSprint?.tasks || [];
          recordAction?.({
            type: 'BRAIN_MODIFY_MISSIONS',
            description: preview.sentence || 'Restructure',
            undo: () => {
              setLocalSprint((prev) => (prev ? { ...prev, tasks: before } : prev));
            },
            redo: () => {
              setLocalSprint((prev) => (prev ? { ...prev, tasks: planned.tasks } : prev));
            },
          });
          setLocalSprint((prev) =>
            prev ? { ...prev, tasks: planned.tasks, updatedAt: new Date().toISOString() } : prev
          );
          schedulePasteAutoLayoutAfterMeasure(
            coreState.flowchartRef,
            planned.createdIds.length ? planned.createdIds : planned.tasks.map((task) => task.id)
          );
          return true;
        }
        if (!preview?.clipboard || !handlers?.handlePasteTasks) return false;
        const prepared = prepareClipboardPaste(preview.clipboard);
        if (!prepared) return false;
        await handlers.handlePasteTasks(prepared);
        schedulePasteAutoLayoutAfterMeasure(
          coreState.flowchartRef,
          prepared.tasks.map((task) => task.id)
        );
        return true;
      },
    });
  }, [register, undo, recordAction, localSprint, handlers, coreState.flowchartRef, setLocalSprint]);

  useSprintDetailIntegrations({
    canUndo,
    canRedo,
    undoStack,
    redoStack,
    undo,
    redo,
    recordAction,
    state,
    viewSwitcherProps,
    onOpenDeleteAllModal: modals.handleOpenDeleteAllModal,
  });

  const renderHandlers = useSprintDetailRenderHandlers({
    modals, actions, tasks: setup.tasks, state, setLocalSprint,
    handleConfirmDeleteTaskWithNested,
  });
  const { handleDeleteModalConfirm } = renderHandlers;

  const campaignVariables = useCampaignVariables({
    localSprint,
    setLocalSprint,
    allSprints,
    loadFullSprint,
    onUpdateSprint,
    isMultiCampaignMode,
  });

  // Must be before early return to satisfy React hooks rules
  const waitNow = useWaitGateNow(setup.tasks);
  const lockStates = useMemo(() => {
    return calculateLockStatesWithParentContext(
      setup.tasks,
      nested.nestedSprints || [],
      setup.statuses,
      {
        currentSprint: localSprint,
        allSprints,
        now: waitNow,
      }
    );
  }, [setup.tasks, nested.nestedSprints, setup.statuses, localSprint, allSprints, waitNow]);

  useWaitUnlockNotifications({
    tasks: setup.tasks,
    lockStates,
    campaignId: localSprint?.id,
    campaignName: localSprint?.name,
    breadcrumbPath: nested.breadcrumbPath,
    enabled: !isTemplate && !isMultiCampaignMode && !!localSprint?.id,
  });

  useMissionReminderWatch({
    tasks: setup.tasks,
    lockStates,
    now: waitNow,
    campaignId: localSprint?.id,
    campaignName: localSprint?.name,
    breadcrumbPath: nested.breadcrumbPath,
    enabled: !isTemplate && !isMultiCampaignMode && !!localSprint?.id,
    onRemindersChange: actions.handleRemindersChange,
  });

  useClockTicketSync({
    tasks: setup.tasks,
    lockStates,
    campaignId: localSprint?.id,
    campaignName: localSprint?.name,
    breadcrumbPath: nested.breadcrumbPath,
    enabled: !isTemplate && !isMultiCampaignMode && !!localSprint?.id,
  });

  const handleCreateCampaignFromCanvas = useCallback((connect) => {
    if (!localSprint?.id || !onCreateNestedCampaign) return;
    const options = connect?.sourceTaskId
      ? { stayHere: true, connect }
      : null;
    onCreateNestedCampaign(localSprint.id, options);
  }, [localSprint?.id, onCreateNestedCampaign]);

  const flowPropsWithCreate = useMemo(() => ({
    ...flowProps,
    onCreateCampaign: (!isMultiCampaignMode && localSprint?.id && onCreateNestedCampaign)
      ? handleCreateCampaignFromCanvas
      : undefined,
  }), [flowProps, isMultiCampaignMode, localSprint?.id, onCreateNestedCampaign, handleCreateCampaignFromCanvas]);

  if (!localSprint && !isMultiCampaignMode) {
    return <div className="sprint-detail__error"><h2>Not found</h2></div>;
  }

  return (
    <SprintDetailContent
      isLoadingContent={isLoadingContent}
      isMultiCampaignMode={isMultiCampaignMode}
      localSprint={localSprint}
      allSprints={allSprints}
      multiCampaignData={multiCampaignData}
      onSelectSprint={onSelectSprint}
      sidebarCollapsed={sidebarCollapsed}
      undoStack={undoStack}
      redoStack={redoStack}
      undo={undo}
      redo={redo}
      undoTo={undoTo}
      redoTo={redoTo}
      undoAll={undoAll}
      clearHistory={clearHistory}
      state={state}
      setup={setup}
      actions={actions}
      handlers={handlers}
      modals={modals}
      nested={nested}
      fokusModeRefs={fokusModeRefs}
      wrappedToolbar={wrappedToolbar}
      handleViewRename={handleViewRename}
      handleViewIconChangeLocal={handleViewIconChangeLocal}
      handleCampaignPickerSelect={handleCampaignPickerSelect}
      handleDeleteTasksWithNestedCheck={handleDeleteTasksWithNestedCheck}
      handleTrashNestedSprint={handleTrashNestedSprint}
      calculatedProgress={calculatedProgress}
      flowProps={flowPropsWithCreate}
      augmentedKanbanProps={augmentedKanbanProps}
      augmentedTableProps={augmentedTableProps}
      todoListProps={todoListProps}
      viewSwitcherProps={viewSwitcherProps}
      neuralFilteredTasks={neuralFilteredTasks}
      inProgressLockWarningModal={inProgressLockWarningModal}
      renameFieldModal={renameFieldModal}
      setRenameFieldModal={setRenameFieldModal}
      handleDeleteModalConfirm={handleDeleteModalConfirm}
      renderHandlers={renderHandlers}
      isVariablesPanelOpen={campaignVariables.isVariablesPanelOpen}
      setIsVariablesPanelOpen={campaignVariables.setIsVariablesPanelOpen}
      variableCampaign={campaignVariables.variableCampaign}
      handleCampaignVariablesChange={campaignVariables.handleCampaignVariablesChange}
      handleInheritedVariablePatch={campaignVariables.handleInheritedVariablePatch}
      handleCampaignVariablesColumnWidthsChange={campaignVariables.handleCampaignVariablesColumnWidthsChange}
      openMission={openMission}
      closeMission={closeMission}
      setMissionDocStack={setMissionDocStack}
      restoredDocStack={restoredDocStack}
      skipSurfaceAnimation={skipSurfaceAnimation}
      consumeSkipSurfaceAnimation={consumeSkipSurfaceAnimation}
      tabOpenMissionId={tabOpenMissionId}
      missionDocDirect={missionDocDirect}
      lockStates={lockStates}
    />
  );
};

export default SprintDetail;
